/**
 * When the split view's numbers describe one settled read of the frame.
 *
 * Public Look 2026-10-22 on /homes-for-sale/bend found two counts that moved
 * on the same map frame:
 *   - the header total printed the server seed's count (721, another load
 *     922) and then the pin read's (1,102) once the map mounted. The seed is
 *     only the frame's first card page, read while the map was not there; the
 *     pin read is the read the page settles on. Printing both is printing a
 *     partial count first.
 *   - the $/sq ft basis ("for N homes on this map that report a living area")
 *     read 22, 24, 17 and 21 across loads and sorts, because the band was
 *     built from whatever rows were in hand: the seed's first page, or the
 *     first 500 of a larger frame in the chosen order. Both depend on sort,
 *     page size and which read landed last.
 *
 * The rule: a count prints once the read it comes from is the one the page
 * settles on, and the band (with its N) is built only from a settled read
 * that holds every home in the frame, so the same frame gives the same N
 * under every sort.
 */
import { buildPpsfBand, type PpsfBand } from '@/components/search/ppsf-band'

export type FrameReadState = {
  /**
   * False until the client has mounted (server render and the hydration
   * pass): whether the map mounts, and so whether a pin read follows, is not
   * known yet.
   */
  hydrated: boolean
  /** The server seeded only part of the frame (its first card page). */
  seedIsPartial: boolean
  /** The seed itself timed out: no pin read follows it. */
  seedDegraded: boolean
  /** The map is mounted (or mounting), so the partial seed gets a pin read. */
  mapWanted: boolean
  /** The pin read for the current seed has finished (or was not needed). */
  pinReadDone: boolean
}

/**
 * True while a read the page will settle on is still owed for the current
 * seed: the header shows its loading state instead of the seed's count.
 */
export function frameCountPending(s: FrameReadState): boolean {
  if (!s.seedIsPartial || s.seedDegraded) return false
  if (!s.hydrated) return true
  return s.mapWanted && !s.pinReadDone
}

/**
 * True when the rows in hand are the whole frame: the count is exact and
 * every counted home was read.
 */
export function rowsHoldWholeFrame(input: {
  rowsInHand: number
  totalCount: number
  countIsExact: boolean
}): boolean {
  return input.countIsExact && input.rowsInHand >= input.totalCount
}

/**
 * The $/sq ft band for the frame, or null when the rows in hand are not one
 * settled read of the whole frame. Its `n` is the basis count the readout
 * prints, so it never comes from a page of rows in some order.
 */
export function settledFrameBand(
  values: readonly (number | null | undefined)[],
  frame: { pending: boolean; wholeFrame: boolean },
): PpsfBand | null {
  if (frame.pending || !frame.wholeFrame) return null
  return buildPpsfBand(values)
}
